import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatDate } from "@/lib/api";
import { toast } from "sonner";
import {
  AlertTriangle,
  Boxes,
  ClipboardCheck,
  PackageCheck,
  RefreshCw,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const movementLabels = {
  purchase_return_pending: "Karantina retur",
  purchase_return_completed: "Retur dikirim",
  purchase_return_cancelled: "Retur dibatalkan",
  initial: "Stok awal",
  sale: "Penjualan",
  purchase: "Pembelian",
  refund: "Refund",
  adjust: "Penyesuaian",
  opname: "Stock opname",
};

const numberText = (value) => Number(value || 0).toLocaleString("id-ID", {
  maximumFractionDigits: 2,
});

export default function Stock() {
  const [outlets, setOutlets] = useState([]);
  const [outletId, setOutletId] = useState("");
  const [overview, setOverview] = useState({ items: [], summary: {} });
  const [movements, setMovements] = useState([]);
  const [opnames, setOpnames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [form, setForm] = useState({ product_id: "", delta: 0, note: "" });
  const [opnameOpen, setOpnameOpen] = useState(false);
  const [opnameRows, setOpnameRows] = useState([]);
  const [opnameNote, setOpnameNote] = useState("");
  const [savingOpname, setSavingOpname] = useState(false);

  useEffect(() => {
    api.get("/outlets")
      .then((response) => {
        const active = (response.data || []).filter((row) => row.active !== false);
        setOutlets(active);
        if (active.length) setOutletId((current) => current || active[0].id);
      })
      .catch((error) => toast.error(error?.response?.data?.detail || "Outlet tidak dapat dimuat"));
  }, []);

  const loadStock = useCallback(async ({ silent = false } = {}) => {
    if (!outletId) return;
    if (!silent) setRefreshing(true);
    try {
      const params = { outlet_id: outletId };
      const [overviewResponse, movementResponse, opnameResponse] = await Promise.all([
        api.get("/stock/overview", { params }),
        api.get("/stock/movements", { params }),
        api.get("/stock/opnames", { params }),
      ]);
      setOverview(overviewResponse.data || { items: [], summary: {} });
      setMovements(movementResponse.data || []);
      setOpnames(opnameResponse.data || []);
      setLastSync(new Date());
      setForm((current) => {
        const exists = (overviewResponse.data?.items || []).some((item) => item.id === current.product_id);
        return exists ? current : { ...current, product_id: "" };
      });
    } catch (error) {
      if (!silent) toast.error(error?.response?.data?.detail || "Data stok tidak dapat dimuat");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [outletId]);

  useEffect(() => {
    if (!outletId) return undefined;
    setLoading(true);
    loadStock();
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") loadStock({ silent: true });
    }, 5000);
    return () => window.clearInterval(timer);
  }, [outletId, loadStock]);

  const products = useMemo(() => overview.items || [], [overview.items]);
  const summary = overview.summary || {};
  const lowProducts = useMemo(
    () => products.filter((product) => product.is_low).sort((a, b) => Number(a.stock) - Number(b.stock)),
    [products],
  );
  const productMap = useMemo(
    () => Object.fromEntries(products.map((product) => [product.id, product])),
    [products],
  );
  const outletMap = useMemo(
    () => Object.fromEntries(outlets.map((outlet) => [outlet.id, outlet])),
    [outlets],
  );

  const adjust = async () => {
    if (!form.product_id || !outletId) return toast.error("Pilih produk dan outlet");
    if (!Number(form.delta)) return toast.error("Jumlah penyesuaian tidak boleh 0");
    try {
      await api.post("/products/stock/adjust", {
        product_id: form.product_id,
        outlet_id: outletId,
        delta: Number(form.delta),
        note: form.note,
      });
      toast.success("Stok disesuaikan");
      setForm({ product_id: "", delta: 0, note: "" });
      await loadStock();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Stok gagal disesuaikan");
    }
  };

  const openOpname = () => {
    if (!products.length) return toast.error("Belum ada produk untuk dihitung");
    setOpnameRows(products.map((product) => ({
      product_id: product.id,
      name: product.name,
      unit: product.unit,
      system_stock: Number(product.stock || 0),
      physical_stock: String(Number(product.stock || 0)),
      note: "",
    })));
    setOpnameNote("");
    setOpnameOpen(true);
  };

  const updateOpnameRow = (index, key, value) => {
    setOpnameRows((rows) => rows.map((row, rowIndex) => (
      rowIndex === index ? { ...row, [key]: value } : row
    )));
  };

  const submitOpname = async () => {
    const invalid = opnameRows.some((row) => row.physical_stock === "" || Number(row.physical_stock) < 0);
    if (invalid) return toast.error("Stok fisik wajib diisi dan tidak boleh negatif");
    setSavingOpname(true);
    try {
      const response = await api.post("/stock/opnames", {
        outlet_id: outletId,
        note: opnameNote,
        items: opnameRows.map((row) => ({
          product_id: row.product_id,
          physical_stock: Number(row.physical_stock),
          note: row.note,
        })),
      });
      toast.success(`Stock opname selesai. ${response.data?.adjusted_items || 0} produk disesuaikan.`);
      setOpnameOpen(false);
      await loadStock();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Stock opname gagal disimpan");
    } finally {
      setSavingOpname(false);
    }
  };

  const cards = [
    { label: "Produk dipantau", value: summary.total_products || 0, icon: Boxes },
    { label: "Total unit", value: numberText(summary.total_units), icon: PackageCheck },
    { label: "Stok rendah", value: summary.low_stock_count || 0, icon: AlertTriangle, warning: summary.low_stock_count > 0 },
    { label: "Stok habis", value: summary.out_of_stock_count || 0, icon: AlertTriangle, warning: summary.out_of_stock_count > 0 },
  ];

  return (
    <div className="p-6 lg:p-10">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between mb-6">
        <div>
          <div className="overline text-zinc-500">INVENTARIS</div>
          <h1 className="font-display font-black text-4xl tracking-tighter">Stok & Pergerakan</h1>
          <div className="text-xs text-zinc-500 mt-2">
            Sinkron otomatis setiap 5 detik
            {lastSync ? ` · terakhir ${lastSync.toLocaleTimeString("id-ID")}` : ""}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <select
            value={outletId}
            onChange={(event) => setOutletId(event.target.value)}
            className="border border-border px-3 py-2.5 text-sm min-w-48"
            data-testid="stock-filter-outlet"
          >
            {outlets.map((outlet) => <option key={outlet.id} value={outlet.id}>{outlet.name}</option>)}
          </select>
          <button
            onClick={() => loadStock()}
            disabled={refreshing || !outletId}
            className="border border-border px-4 py-2.5 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button
            onClick={openOpname}
            disabled={!outletId || !products.length}
            className="bg-[#002FA7] text-white px-4 py-2.5 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="stock-opname-open"
          >
            <ClipboardCheck className="h-4 w-4" /> Stock Opname
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {cards.map((card) => (
          <div key={card.label} className={`border p-4 bg-white ${card.warning ? "border-rose-300 bg-rose-50" : "border-border"}`}>
            <div className="flex items-center justify-between text-xs uppercase tracking-wider text-zinc-500">
              {card.label}<card.icon className={`h-4 w-4 ${card.warning ? "text-rose-600" : "text-[#002FA7]"}`} />
            </div>
            <div className={`mt-2 font-display font-black text-3xl tabular-nums ${card.warning ? "text-rose-700" : ""}`}>
              {loading ? "…" : card.value}
            </div>
          </div>
        ))}
      </div>

      {lowProducts.length > 0 && (
        <div className="mb-6 border border-amber-300 bg-amber-50 p-5">
          <div className="flex items-center gap-2 font-semibold text-amber-900">
            <AlertTriangle className="h-5 w-5" /> Peringatan stok rendah
          </div>
          <div className="mt-3 grid md:grid-cols-2 xl:grid-cols-3 gap-2">
            {lowProducts.map((product) => (
              <div key={product.id} className="bg-white border border-amber-200 px-3 py-2 flex justify-between gap-3 text-sm">
                <span className="font-semibold truncate">{product.name}</span>
                <span className={product.is_out ? "text-rose-700 font-bold" : "text-amber-800 font-semibold"}>
                  {numberText(product.stock)} / min. {numberText(product.min_stock)} {product.unit}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-4 mb-8">
        <div className="lg:col-span-2 border border-border bg-white overflow-x-auto">
          <div className="p-5 border-b border-border">
            <div className="overline text-zinc-500">STOK TERKINI</div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-border">
              <tr>
                <th className="text-left p-3 overline">Produk</th>
                <th className="text-left p-3 overline">SKU</th>
                <th className="text-right p-3 overline">Stok</th>
                <th className="text-right p-3 overline">Minimum</th>
                <th className="text-left p-3 overline">Status</th>
              </tr>
            </thead>
            <tbody>
              {!products.length && <tr><td colSpan={5} className="p-8 text-center text-zinc-500">Belum ada produk</td></tr>}
              {products.map((product) => (
                <tr key={product.id} className="border-b border-border">
                  <td className="p-3 font-semibold">{product.name}</td>
                  <td className="p-3 text-xs font-mono text-zinc-500">{product.sku || product.barcode || "-"}</td>
                  <td className="p-3 text-right tabular-nums font-semibold">{numberText(product.stock)} {product.unit}{Number(product.quarantined_stock || 0) > 0 && <div className="text-xs text-amber-700">Karantina: {numberText(product.quarantined_stock)} · Penjualan diblokir</div>}</td>
                  <td className="p-3 text-right tabular-nums text-zinc-500">{numberText(product.min_stock)}</td>
                  <td className="p-3">
                    <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 ${
                      product.is_out
                        ? "bg-rose-100 text-rose-800"
                        : product.is_low
                          ? "bg-amber-100 text-amber-800"
                          : "bg-emerald-100 text-emerald-800"
                    }`}>
                      {product.is_out ? "Habis" : product.is_low ? "Rendah" : "Aman"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="border border-border p-5 bg-white" data-testid="stock-adjust-panel">
          <div className="overline text-zinc-500 mb-4">PENYESUAIAN MANUAL</div>
          <div className="space-y-3">
            <div>
              <label className="overline">Produk</label>
              <select
                data-testid="stock-product"
                value={form.product_id}
                onChange={(event) => setForm({ ...form, product_id: event.target.value })}
                className="w-full border border-border px-3 py-2 mt-1"
              >
                <option value="">— pilih —</option>
                {products.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name} (stok {numberText(product.stock)})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="overline">Jumlah (+/-)</label>
              <input
                data-testid="stock-delta"
                type="number"
                value={form.delta}
                onChange={(event) => setForm({ ...form, delta: event.target.value })}
                className="w-full border border-border px-3 py-2 mt-1 font-mono-num"
              />
            </div>
            <div>
              <label className="overline">Catatan</label>
              <input
                data-testid="stock-note"
                value={form.note}
                onChange={(event) => setForm({ ...form, note: event.target.value })}
                className="w-full border border-border px-3 py-2 mt-1"
                placeholder="Contoh: barang rusak"
              />
            </div>
            <button
              onClick={adjust}
              data-testid="stock-adjust-btn"
              className="w-full bg-zinc-900 text-white py-3 font-semibold hover:bg-zinc-700"
            >
              Sesuaikan
            </button>
          </div>
        </div>
      </div>

      <div className="border border-border bg-white mb-8 overflow-x-auto">
        <div className="p-5 border-b border-border"><div className="overline text-zinc-500">RIWAYAT PERGERAKAN</div></div>
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-border"><tr>
            <th className="text-left p-3 overline">Waktu</th>
            <th className="text-left p-3 overline">Produk</th>
            <th className="text-left p-3 overline">Tipe</th>
            <th className="text-right p-3 overline">Sebelum</th>
            <th className="text-right p-3 overline">Perubahan</th>
            <th className="text-right p-3 overline">Sesudah</th>
            <th className="text-left p-3 overline">Catatan</th>
          </tr></thead>
          <tbody>
            {!movements.length && <tr><td colSpan={7} className="p-8 text-center text-zinc-500">Belum ada pergerakan</td></tr>}
            {movements.map((movement) => (
              <tr key={movement.id} className="border-b border-border">
                <td className="p-3 text-xs text-zinc-500">{formatDate(movement.created_at)}</td>
                <td className="p-3 font-semibold">{productMap[movement.product_id]?.name || movement.product_id}</td>
                <td className="p-3 text-xs uppercase tracking-wider font-semibold">{movementLabels[movement.type] || movement.type}</td>
                <td className="p-3 text-right tabular-nums text-zinc-500">{movement.balance_before ?? "-"}</td>
                <td className={`p-3 text-right tabular-nums font-semibold ${Number(movement.delta) > 0 ? "text-emerald-700" : "text-rose-700"}`}>
                  {Number(movement.delta) > 0 ? "+" : ""}{numberText(movement.delta)}
                </td>
                <td className="p-3 text-right tabular-nums">{numberText(movement.balance_after)}</td>
                <td className="p-3 text-xs text-zinc-500 max-w-56 truncate">{movement.note || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="border border-border bg-white overflow-x-auto">
        <div className="p-5 border-b border-border"><div className="overline text-zinc-500">RIWAYAT STOCK OPNAME</div></div>
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-border"><tr>
            <th className="text-left p-3 overline">Waktu</th>
            <th className="text-left p-3 overline">Outlet</th>
            <th className="text-left p-3 overline">Petugas</th>
            <th className="text-right p-3 overline">Produk dihitung</th>
            <th className="text-right p-3 overline">Disesuaikan</th>
            <th className="text-left p-3 overline">Catatan</th>
          </tr></thead>
          <tbody>
            {!opnames.length && <tr><td colSpan={6} className="p-8 text-center text-zinc-500">Belum ada stock opname</td></tr>}
            {opnames.map((opname) => (
              <tr key={opname.id} className="border-b border-border">
                <td className="p-3 text-xs text-zinc-500">{formatDate(opname.created_at)}</td>
                <td className="p-3">{outletMap[opname.outlet_id]?.name || "-"}</td>
                <td className="p-3">{opname.actor_name || "-"}</td>
                <td className="p-3 text-right tabular-nums">{opname.total_items || 0}</td>
                <td className="p-3 text-right tabular-nums font-semibold">{opname.adjusted_items || 0}</td>
                <td className="p-3 text-zinc-500">{opname.note || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={opnameOpen} onOpenChange={setOpnameOpen}>
        <DialogContent className="rounded-none max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Stock Opname — {outletMap[outletId]?.name || "Outlet"}</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-zinc-500">
            Masukkan jumlah fisik hasil perhitungan. Sistem otomatis mencatat selisih sebagai pergerakan stok.
          </div>
          <div className="border border-border overflow-x-auto mt-3">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-border"><tr>
                <th className="text-left p-3 overline">Produk</th>
                <th className="text-right p-3 overline">Stok Sistem</th>
                <th className="text-right p-3 overline">Stok Fisik</th>
                <th className="text-right p-3 overline">Selisih</th>
                <th className="text-left p-3 overline">Catatan</th>
              </tr></thead>
              <tbody>
                {opnameRows.map((row, index) => {
                  const difference = Number(row.physical_stock || 0) - Number(row.system_stock || 0);
                  return (
                    <tr key={row.product_id} className="border-b border-border">
                      <td className="p-3 font-semibold">{row.name}</td>
                      <td className="p-3 text-right tabular-nums">{numberText(row.system_stock)} {row.unit}</td>
                      <td className="p-3 text-right">
                        <input
                          type="number"
                          min="0"
                          value={row.physical_stock}
                          onChange={(event) => updateOpnameRow(index, "physical_stock", event.target.value)}
                          className="w-28 border border-border px-2 py-1.5 text-right font-mono-num"
                          data-testid={`opname-physical-${row.product_id}`}
                        />
                      </td>
                      <td className={`p-3 text-right tabular-nums font-semibold ${difference > 0 ? "text-emerald-700" : difference < 0 ? "text-rose-700" : "text-zinc-500"}`}>
                        {difference > 0 ? "+" : ""}{numberText(difference)}
                      </td>
                      <td className="p-3">
                        <input
                          value={row.note}
                          onChange={(event) => updateOpnameRow(index, "note", event.target.value)}
                          className="w-full min-w-40 border border-border px-2 py-1.5"
                          placeholder="Opsional"
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div>
            <label className="overline">Catatan Umum</label>
            <textarea
              value={opnameNote}
              onChange={(event) => setOpnameNote(event.target.value)}
              rows={2}
              className="w-full border border-border px-3 py-2 mt-1"
              placeholder="Contoh: opname akhir bulan"
            />
          </div>
          <div className="flex gap-2">
            <button onClick={() => setOpnameOpen(false)} disabled={savingOpname} className="flex-1 py-3 border border-border disabled:opacity-50">Batal</button>
            <button onClick={submitOpname} disabled={savingOpname} className="flex-1 py-3 bg-[#002FA7] text-white font-semibold disabled:opacity-50" data-testid="stock-opname-save">
              {savingOpname ? "Menyimpan…" : "Simpan Stock Opname"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
